// src/pages/Home/Home.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../context/DataContext';
import LiveRates from './components/LiveRates';
import { WifiOff, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import './Home.css';

const Home = () => {
  const { rates, loading, isConnected, lastUpdate, error, usingFallback } = useData();
  const [activeTab, setActiveTab] = useState('silver');

  useEffect(() => {
    console.log('Home Component Rendered:', { rates });
  }, [rates]);

  const statusVariants = {
    connected: { 
      background: 'var(--gradient-primary)',
      boxShadow: '0 4px 20px rgba(236, 72, 153, 0.2)'
    },
    disconnected: {
      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      boxShadow: '0 4px 20px rgba(220, 38, 38, 0.2)'
    },
    fallback: {
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.2)'
    }
  };

  const currentStatus = usingFallback ? 'fallback' : (isConnected ? 'connected' : 'disconnected');

  // Filter rates by metal
  const silverRates = rates?.filter(r => r.metal === 'Silver') || [];
  const goldRates = rates?.filter(r => r.metal === 'Gold') || [];

  return (
    <motion.div 
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Connection Status Bar */}
      <motion.div 
        className={`connection-status ${currentStatus}`}
        variants={statusVariants}
        initial="disconnected"
        animate={currentStatus}
        transition={{ duration: 0.5 }}
      >
        <div className="container-custom">
          <div className="status-content">
            <motion.div 
              className="status-icon"
              animate={{ rotate: isConnected ? 0 : 360 }}
              transition={{ duration: 2, repeat: isConnected ? 0 : Infinity }}
            >
              {isConnected ? (
                <CheckCircle size={16} className="status-icon-success" />
              ) : (
                <WifiOff size={16} className="status-icon-error" />
              )}
            </motion.div>
            
            <span className="status-text">
              {isConnected ? (
                usingFallback ? (
                  <span className="status-fallback-text">
                    <Zap size={14} className="inline" />
                    Fallback Mode - Using cached data
                  </span>
                ) : (
                  <span className="status-live-text">
                    <span className="status-dot"></span>
                    Live Feed Connected
                  </span>
                )
              ) : (
                'Connecting to feed...'
              )}
            </span>

            <AnimatePresence>
              {usingFallback && (
                <motion.span 
                  className="status-fallback-badge"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                >
                  ⚡ Fallback
                </motion.span>
              )}
            </AnimatePresence>

            {lastUpdate && (
              <span className="status-time">
                Updated: {lastUpdate.toLocaleTimeString()}
              </span>
            )}

            {error && (
              <motion.span 
                className="status-error"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <AlertCircle size={14} />
                {error}
              </motion.span>
            )}
          </div>
        </div>
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        <LiveRates 
          rates={rates} 
          loading={loading}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          silverRates={silverRates}
          goldRates={goldRates}
        />
      </motion.div>
    </motion.div>
  );
};

export default Home;